import React from 'react';
import { AdData, Finding } from './types';
interface AdPreviewProps {
  ad: AdData;
  compact?: boolean;
  blockers?: Finding[];
  suggestions?: Finding[];
}
function IndicatorBadge({
  items,
  color,
  bgColor,
  compact
}: {
  items: Finding[];
  color: string;
  bgColor: string;
  compact?: boolean;
}) {
  if (!items.length) return null;
  return <div style={{
    position: 'absolute',
    top: compact ? -4 : -6,
    right: compact ? -4 : -6,
    display: 'flex',
    gap: 3,
    zIndex: 5
  }} data-id="element-40">
      {items.map((f, i) => <div key={f.id} title={f.title} style={{
      width: compact ? 16 : 20,
      height: compact ? 16 : 20,
      borderRadius: '50%',
      background: color,
      border: '2px solid white',
      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: compact ? 7 : 8.5,
      fontWeight: 800,
      color: 'white',
      cursor: 'default'
    }} data-id="element-41">
          {items.length > 1 ? i + 1 : '!'}
        </div>)}
    </div>;
}
export function AdPreview({
  ad,
  compact,
  blockers = [],
  suggestions = []
}: AdPreviewProps) {
  const p = compact ? '14px 12px' : '22px 18px';
  // Map findings to ad sections by location
  const getFindings = (location: string) => {
    const b = blockers.filter(f => f.location === location);
    const s = suggestions.filter(f => f.location === location);
    return {
      blockers: b,
      suggestions: s,
      hasAny: b.length > 0 || s.length > 0
    };
  };
  const perfHero = getFindings('Performance hero');
  const testimonial = getFindings('Testimonial');
  const headline = getFindings('Headline');
  const credentials = getFindings('Credentials');
  const bodyCopy = getFindings('Body copy');
  const footer = getFindings('Footer');
  const sectionHighlight = (section: {
    blockers: Finding[];
    suggestions: Finding[];
    hasAny: boolean;
  }) => {
    if (!section.hasAny) return {};
    if (section.blockers.length > 0) return {
      boxShadow: 'inset 0 0 0 1.5px #E8A0A0',
      borderRadius: 4,
      position: 'relative' as const
    };
    return {
      boxShadow: 'inset 0 0 0 1.5px #F6D68A',
      borderRadius: 4,
      position: 'relative' as const
    };
  };
  return <div style={{
    background: 'white',
    borderRadius: compact ? 10 : 12,
    overflow: 'visible',
    border: '1px solid #E0DDD8',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
  }} data-id="element-42">
      {/* Header */}
      <div style={{
      background: '#1B2A4A',
      padding: compact ? '10px 12px' : '14px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      borderRadius: headline.hasAny ? undefined : undefined,
      ...(headline.hasAny ? {
        outline: '1.5px solid #E8A0A0',
        outlineOffset: -1.5
      } : {})
    }} data-id="element-43">
        {headline.hasAny && <IndicatorBadge items={[...headline.blockers, ...headline.suggestions]} color={headline.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={headline.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-44" />}
        <div data-id="element-45">
          <div style={{
          color: 'white',
          fontSize: compact ? 10 : 13,
          fontWeight: 700
        }} data-id="element-46">
            Meridian Wealth Partners
          </div>
          <div style={{
          color: '#94B4D4',
          fontSize: compact ? 7 : 9,
          marginTop: 1
        }} data-id="element-47">
            Award-Winning Wealth Mgmt{ad.awardNote ? '*' : ''}
          </div>
        </div>
        <div style={{
        color: '#C9A96E',
        fontSize: compact ? 9 : 11,
        fontWeight: 700
      }} data-id="element-48">
          Schedule →
        </div>
      </div>

      {/* Performance */}
      <div style={{
      padding: p,
      textAlign: 'center',
      borderBottom: '1px solid #F0EDE8',
      position: 'relative',
      ...sectionHighlight(perfHero)
    }} data-id="element-49">
        {perfHero.hasAny && <IndicatorBadge items={[...perfHero.blockers, ...perfHero.suggestions]} color={perfHero.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={perfHero.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-50" />}
        <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'center',
        gap: 4
      }} data-id="element-51">
          <span style={{
          fontSize: compact ? 30 : 42,
          fontWeight: 800,
          color: '#1B2A4A',
          lineHeight: 1
        }} data-id="element-52">
            18.7%
          </span>
          <span style={{
          fontSize: compact ? 11 : 14,
          fontWeight: 600,
          color: '#1B2A4A'
        }} data-id="element-53">
            {ad.hasNet ? 'gross return' : 'return'}
          </span>
        </div>
        {ad.hasNet && <div style={{
        marginTop: 4,
        fontSize: compact ? 12 : 16,
        fontWeight: 700,
        color: '#4A6B8A'
      }} data-id="element-54">
            {ad.netReturn}
          </div>}
        {ad.period && <div style={{
        marginTop: 6,
        fontSize: compact ? 7.5 : 9.5,
        color: '#8896A6'
      }} data-id="element-55">
            {ad.strategy} · {ad.period} · Benchmark ({ad.benchmark})
          </div>}
      </div>

      {/* Body */}
      <div style={{
      padding: p
    }} data-id="element-56">
        <div style={{
        fontSize: compact ? 8.5 : 11,
        color: '#4A5B6C',
        lineHeight: 1.65,
        marginBottom: compact ? 8 : 12,
        position: 'relative',
        ...sectionHighlight(bodyCopy),
        ...(bodyCopy.hasAny ? {
          padding: '4px 6px',
          margin: `0 -6px ${compact ? 8 : 12}px`
        } : {})
      }} data-id="element-57">
          {bodyCopy.hasAny && <IndicatorBadge items={[...bodyCopy.blockers, ...bodyCopy.suggestions]} color={bodyCopy.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={bodyCopy.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-58" />}
          Strategies tailored to individual client objectives.
        </div>

        {/* Testimonial */}
        <div style={{
        padding: compact ? '7px 9px' : '10px 14px',
        background: '#F8F7F4',
        borderRadius: 8,
        borderLeft: `3px solid ${testimonial.blockers.length > 0 ? '#B44D4D' : '#C9A96E'}`,
        marginBottom: compact ? 8 : 12,
        position: 'relative',
        ...(testimonial.hasAny ? {
          boxShadow: `inset 0 0 0 1.5px ${testimonial.blockers.length > 0 ? '#E8A0A0' : '#F6D68A'}`
        } : {})
      }} data-id="element-59">
          {testimonial.hasAny && <IndicatorBadge items={[...testimonial.blockers, ...testimonial.suggestions]} color={testimonial.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={testimonial.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-60" />}
          <div style={{
          fontSize: compact ? 8 : 10.5,
          color: '#3D4F5F',
          lineHeight: 1.6,
          fontStyle: 'italic'
        }} data-id="element-61">
            "Meridian transformed my outlook."
          </div>
          <div style={{
          fontSize: compact ? 7 : 9,
          color: '#8896A6',
          marginTop: 3,
          fontWeight: 600
        }} data-id="element-62">
            — Sarah M., client
          </div>
          {ad.testimonialDisc && <div style={{
          fontSize: compact ? 5.5 : 7,
          color: '#A0AEC0',
          marginTop: 5,
          lineHeight: 1.5
        }} data-id="element-63">
              Current client. Not compensated. Not representative.
            </div>}
        </div>

        {/* Credentials */}
        <div style={{
        display: 'flex',
        gap: compact ? 5 : 8,
        flexWrap: 'wrap',
        position: 'relative',
        ...sectionHighlight(credentials),
        ...(credentials.hasAny ? {
          padding: '4px 6px',
          margin: '0 -6px'
        } : {})
      }} data-id="element-64">
          {credentials.hasAny && <IndicatorBadge items={[...credentials.blockers, ...credentials.suggestions]} color={credentials.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={credentials.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-65" />}
          {[ad.aum, 'Est. 2003', ad.awardNote ? "Barron's Top Advisor*" : "Barron's Top Advisor"].map((c, i) => <span key={i} style={{
          padding: compact ? '2px 5px' : '3px 8px',
          background: '#F0EDE8',
          borderRadius: 4,
          fontSize: compact ? 6.5 : 8.5,
          color: '#5A6B7C',
          fontWeight: 600
        }} data-id="element-66">
              {c}
            </span>)}
        </div>
      </div>

      {/* Footer */}
      <div style={{
      padding: compact ? '6px 12px' : '8px 18px',
      borderTop: '1px solid #F0EDE8',
      background: '#FAFAF8',
      position: 'relative',
      ...sectionHighlight(footer)
    }} data-id="element-67">
        {footer.hasAny && <IndicatorBadge items={[...footer.blockers, ...footer.suggestions]} color={footer.blockers.length > 0 ? '#B44D4D' : '#D97706'} bgColor={footer.blockers.length > 0 ? '#FEF2F2' : '#FFFBEB'} compact={compact} data-id="element-68" />}
        <div style={{
        fontSize: compact ? 4.5 : 6.5,
        color: '#A0AEC0',
        lineHeight: 1.5
      }} data-id="element-69">
          {ad.footer}
        </div>
      </div>
    </div>;
}